import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { generateTempId } from '../lib/offline/offlineDb';
import { useOfflineMutation } from './useOfflineMutation';
import type { Product } from '../types/product';

export type CartProduct = Product & {
  pivot: { sort_order: number };
};

export interface ApiCart {
  id: number;
  name: string;
  products: CartProduct[];
  /** Set once the document went out to a customer — it can no longer be changed. */
  lockedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useCarts(enabled = true) {
  return useQuery<ApiCart[]>({
    queryKey: ['carts'],
    queryFn: () => api<ApiCart[]>('/carts'),
    enabled,
    // Only poll while online — an offline queryFn call returns the network
    // layer's stale snapshot and would clobber optimistic mutations the
    // user already made but not yet synced (VIVATISPDB-305).
    refetchInterval: () => (typeof navigator !== 'undefined' && navigator.onLine ? 5 * 60 * 1000 : false),
  });
}

export function useCreateCart() {
  const queryClient = useQueryClient();

  return useOfflineMutation<string, ApiCart | void>({
    operationType: 'CART_CREATE',
    feature: 'cart',
    needsTempId: true,
    mutationFn: (name) =>
      api<ApiCart>('/carts', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    toPayload: (name) => ({ name }),
    invalidateKeys: [['carts']],
    onMutate: async (name, tempId) => {
      const id = tempId ?? generateTempId();
      const newCart: ApiCart = {
        id: id as unknown as number,
        name,
        products: [],
        lockedAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) => [...old, newCart]);
      return { previous: undefined };
    },
    // Swap the optimistic row for the server one immediately. Until the
    // refetch from onSettled lands the cache would otherwise only know the
    // tempId, so anything looking up the freshly created cart by its real id
    // — the active-document guard in useCartActions above all — would not
    // find it and fall back to another document (VIVATISPDB-443).
    onSuccess: (created, _name, context) => {
      if (!created || !context?.tempId) {
        return;
      }
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) =>
        old.map((c) => (String(c.id) === context.tempId ? created : c)),
      );
    },
  });
}

export function useRenameCart() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ cartId: number; name: string }>({
    operationType: 'CART_RENAME',
    feature: 'cart',
    mutationFn: ({ cartId, name }) =>
      api(`/carts/${cartId}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      }),
    toPayload: ({ cartId, name }) => ({ cartId, name }),
    invalidateKeys: [['carts']],
    onMutate: async ({ cartId, name }) => {
      const previous = queryClient.getQueryData<ApiCart[]>(['carts']);
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) =>
        old.map((c) => (c.id === cartId ? { ...c, name } : c)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['carts'], context.previous);
      }
    },
  });
}

export function useArchiveCart() {
  const queryClient = useQueryClient();

  return useOfflineMutation<number>({
    operationType: 'CART_ARCHIVE',
    feature: 'cart',
    mutationFn: (cartId) =>
      api(`/carts/${cartId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: false }),
      }),
    toPayload: (cartId) => ({ cartId }),
    invalidateKeys: [['carts']],
    onMutate: async (cartId) => {
      const previous = queryClient.getQueryData<ApiCart[]>(['carts']);
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) =>
        old.filter((c) => c.id !== cartId),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['carts'], context.previous);
      }
    },
  });
}

export function useDeleteCart() {
  const queryClient = useQueryClient();

  return useOfflineMutation<number>({
    operationType: 'CART_DELETE',
    feature: 'cart',
    mutationFn: (cartId) => api(`/carts/${cartId}`, { method: 'DELETE' }),
    toPayload: (cartId) => ({ cartId }),
    invalidateKeys: [['carts']],
    onMutate: async (cartId) => {
      const previous = queryClient.getQueryData<ApiCart[]>(['carts']);
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) =>
        old.filter((c) => c.id !== cartId),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['carts'], context.previous);
      }
    },
  });
}

export function useAddProductToCart() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ cartId: number; productId: number }>({
    operationType: 'CART_ADD_PRODUCT',
    feature: 'cart',
    mutationFn: ({ cartId, productId }) =>
      api(`/carts/${cartId}/products`, {
        method: 'POST',
        body: JSON.stringify({ product_id: productId }),
      }),
    toPayload: ({ cartId, productId }) => ({ cartId, productId }),
    invalidateKeys: [['carts']],
    onMutate: async ({ cartId, productId }) => {
      await queryClient.cancelQueries({ queryKey: ['carts'] });
      const previous = queryClient.getQueryData<ApiCart[]>(['carts']);
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) =>
        old.map((c) =>
          c.id === cartId && !c.products.some((p) => p.id === productId)
            ? {
                ...c,
                products: [
                  ...c.products,
                  { id: productId, name: '', articleNumber: '', pivot: { sort_order: c.products.length } } as CartProduct,
                ],
              }
            : c,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['carts'], context.previous);
      }
    },
  });
}

export function useAddProductsToCart() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ cartId: number; productIds: number[] }>({
    operationType: 'CART_ADD_PRODUCTS_BATCH',
    feature: 'cart',
    mutationFn: ({ cartId, productIds }) =>
      api(`/carts/${cartId}/products`, {
        method: 'POST',
        body: JSON.stringify({ product_ids: productIds }),
      }),
    toPayload: ({ cartId, productIds }) => ({ cartId, productIds }),
    invalidateKeys: [['carts']],
    onMutate: async ({ cartId, productIds }) => {
      await queryClient.cancelQueries({ queryKey: ['carts'] });
      const previous = queryClient.getQueryData<ApiCart[]>(['carts']);
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) =>
        old.map((c) => {
          if (c.id !== cartId) return c;
          const existing = new Set(c.products.map((p) => p.id));
          const newProducts = productIds
            .filter((id) => !existing.has(id))
            .map(
              (id, i) =>
                ({ id, name: '', articleNumber: '', pivot: { sort_order: c.products.length + i } }) as CartProduct,
            );
          return { ...c, products: [...c.products, ...newProducts] };
        }),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['carts'], context.previous);
      }
    },
  });
}

export function useRemoveProductFromCart() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ cartId: number; productId: number }>({
    operationType: 'CART_REMOVE_PRODUCT',
    feature: 'cart',
    mutationFn: ({ cartId, productId }) =>
      api(`/carts/${cartId}/products/${productId}`, { method: 'DELETE' }),
    toPayload: ({ cartId, productId }) => ({ cartId, productId }),
    invalidateKeys: [['carts']],
    onMutate: async ({ cartId, productId }) => {
      await queryClient.cancelQueries({ queryKey: ['carts'] });
      const previous = queryClient.getQueryData<ApiCart[]>(['carts']);
      queryClient.setQueryData<ApiCart[]>(['carts'], (old = []) =>
        old.map((c) =>
          c.id === cartId ? { ...c, products: c.products.filter((p) => p.id !== productId) } : c,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['carts'], context.previous);
      }
    },
  });
}
